import React, { useState, useEffect } from 'react';
import '../css/TripList.css';
import { Link } from 'react-router-dom';

const TripList = ({ trips = {}, error }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    const initialExpanded = {
      draft: true,
      planned: true,
      published: true,
      finished: true,
    };
    setExpandedGroups(initialExpanded);
  }, []);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const tripStatusGroups = {
    published: [3],
    planned: [2],
    draft: [1],
    internalReady: [5],
    internalDraft: [4],
  };

  const groupOrder = ['published', 'planned', 'draft'];

  const groupByStatus = (trips) => {
    const groups = {
      draft: [],
      planned: [],
      published: [],
    };

    for (const trip of trips) {
      const status = trip.status;
      const isInternal = [4, 5].includes(status);
      const enrichedTrip = { ...trip, isInternal };

      if (tripStatusGroups.published.includes(status) || status === 5) {
        groups.published.push(enrichedTrip);
      } else if (tripStatusGroups.planned.includes(status)) {
        groups.planned.push(enrichedTrip);
      } else if (tripStatusGroups.draft.includes(status) || status === 4) {
        groups.draft.push(enrichedTrip);
      }
    }

    return groups;
  };

  const tripStatusLabels = {
    published: 'Opublikowane',
    planned: 'Planowane',
    draft: 'Nieopublikowane',
    internalReady: 'Wewnętrzne gotowe',
    internalDraft: 'Wewnętrzne w przygotowaniu',
  };

  const mergeAndDeduplicate = (...lists) => {
    const seen = new Set();
    return lists.flat().filter((trip) => {
      if (seen.has(trip.guid)) return false;
      seen.add(trip.guid);
      return true;
    });
  };

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const getFiltered = () => {
    if (!trips) return [];

    let all = [];

    switch (activeFilter) {
      case 'created':
        all = trips.createdByMe || [];
        break;
      case 'mentor':
        all = trips.asMentor || [];
        break;
      case 'coordinator':
        all = trips.asCoordinator || [];
        break;
      case 'access':
        all = trips.accessGranted || [];
        break;
      default:
        all = mergeAndDeduplicate(
          trips.createdByMe || [],
          trips.asMentor || [],
          trips.asCoordinator || [],
          trips.accessGranted || []
        );
        break;
    }

    const withDate = all
      .filter((t) => t.startDate)
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

    const withoutDate = all
      .filter((t) => !t.startDate)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    return [...withDate, ...withoutDate].filter((t) =>
      t.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const splitActiveAndFinished = (trips) => {
    const now = new Date();
    const active = [];
    const finished = [];

    for (const trip of trips) {
      const end = trip.endDate ? new Date(trip.endDate) : null;

      if (end && end < now) {
        finished.push(trip);
      } else {
        active.push(trip);
      }
    }

    return { active, finished };
  };


  const getRibbonModifierFromNumber = (val) => {
    const n = Number(val);
    switch (n) {
      case 1: return 'ribbon--green';
      case 2: return 'ribbon--orange';
      case 3: return 'ribbon--red';
      default: return '';
    }
  };

  const { active: activeTrips, finished: finishedTrips } = splitActiveAndFinished(getFiltered());

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  return (
    <>
      <div className="trip-filter-wrapper">
        <div className="trip-filter-dropdown">
          <select
            id="filterSelect"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="all">Wszystkie</option>
            <option value="created">Utworzone przeze mnie</option>
            <option value="mentor">Mentorowane</option>
            <option value="coordinator">Koordynowane</option>
            <option value="access">Z udzielonym dostępem</option>
          </select>
        </div>
        <input
          type="text"
          className="trip-search-input"
          placeholder="Szukaj po nazwie..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {activeTrips.length === 0 && finishedTrips.length === 0 ? (
        <p className="no-trips-message">Brak wycieczek do wyświetlenia.</p>
      ) : (
        <>
          <section className="trips-list">
            {groupOrder.map((groupKey) => {
              const tripsInGroup = groupByStatus(activeTrips)[groupKey];
              return (
                <div key={groupKey} className="trip-group">
                  <h3
                    className="trip-group-header"
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <span className="trip-group-toggle">
                      <i
                        className={`fas ${
                          expandedGroups[groupKey] !== false
                            ? 'fa-chevron-down'
                            : 'fa-chevron-right'
                        } trip-group-icon`}
                      ></i>{' '}
                      {tripStatusLabels[groupKey]}
                    </span>
                  </h3>

                  {expandedGroups[groupKey] !== false && (
                    <div className="trips-list">
                      {tripsInGroup
                        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                        .map((trip) => {
                          const showRegistration =
                            groupKey === 'published' &&
                            trip.registrationRibbon !== null &&
                            Number(trip.registrationRibbon) > 0;

                          const registrationClasses = showRegistration
                            ? `has-registration ${getRibbonModifierFromNumber(trip.registrationRibbon)}`
                            : '';

                          return (
                            <Link
                              key={`${trip.guid}-${activeFilter}`}
                              className={`trip-card ${trip.tripSettlementStatus === 4 ? 'settled' : trip.tripSettlementStatus === 5 ? 'archived' : '' } ${registrationClasses}`}
                              to={`/trips/${trip.guid}`}
                            >

                              <div className="trip-thumbnail">
                                {trip.photoThumbnailLink ? (
                                  <img
                                    src={`${trip.photoThumbnailLink}?t=${Date.now()}`}
                                    alt={`Miniaturka dla ${trip.name}`}
                                    className="trip-thumbnail-image"
                                  />
                                ) : (
                                  <i className="fas fa-image trip-thumbnail-icon"></i>
                                )}
                              {trip.isInternal && (
                                <div className="thumb-lock" title="Wycieczka wewnętrzna">
                                  <i className="fas fa-lock"></i>
                                </div>
                              )}
                                
                              </div>

                              <div className="trip-details">
                                <div className="trip-name-section">
                                  <span className="trip-name">
                                    {trip.name?.toUpperCase()}
                                  </span>
                                </div>
                                {trip.startDate && trip.endDate && (
                                  <div className="trip-dates">
                                    {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                                  </div>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          <section className="finished-trips">
            <h3
              className="trip-group-header"
              onClick={() => toggleGroup('finished')}
            >
              <span className="trip-group-toggle">
                <i
                  className={`fas ${
                    expandedGroups.finished !== false
                      ? 'fa-chevron-down'
                      : 'fa-chevron-right'
                  } trip-group-icon`}
                ></i>{' '}
                Zakończone wycieczki
              </span>
            </h3>

            {expandedGroups.finished !== false && (
              <div className="trips-list">
                {finishedTrips
                  .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))
                  .map((trip) => (
                    <Link
                      key={`${trip.guid}-${activeFilter}`}
                      className={`trip-card ${trip.tripSettlementStatus === 4 ? 'settled' : trip.tripSettlementStatus === 5 ? 'archived' : '' }`}
                      to={`/trips/${trip.guid}`}
                    >
                      <div className="trip-thumbnail">
                        {trip.photoThumbnailLink ? (
                          <img
                            src={`${trip.photoThumbnailLink}?t=${Date.now()}`}
                            alt={`Miniaturka dla ${trip.name}`}
                            className="trip-thumbnail-image"
                          />
                        ) : (
                          <i className="fas fa-image trip-thumbnail-icon"></i>
                        )}
                        {trip.isInternal && (
                          <div className="thumb-lock" title="Wycieczka wewnętrzna">
                            <i className="fas fa-lock"></i>
                          </div>
                        )}
                      </div>
                      <div className="trip-details">
                        <div className="trip-name-section">
                          <span className="trip-name">
                            {trip.name?.toUpperCase()}
                          </span>
                        </div>
                        {trip.startDate && trip.endDate && (
                          <div className="trip-dates">
                            {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
};

export default TripList;
